import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as THREE from 'three';
import { createLotusModel, collectDisposables } from './lotusModel';

function randomBetween(min, max) {
    return Math.random() * (max - min) + min;
}

/**
 * 飄落蓮花特效 - 真正的立體 3D 模型（非平面貼圖）
 * Falling lotus flowers - genuine 3D geometry, not a flat billboard texture
 *
 * 每片花瓣是用 Three.js ExtrudeGeometry 擠出、再彎出弧度的曲面幾何體，
 * 多層環狀排列組成整朵花（見 lotusModel.js）。因為是真實的立體形狀，
 * 從任何角度旋轉觀察都正確，不會像平面貼圖那樣轉到側面就變成一條線消失。
 */
const FallingLotusModel3D = ({
    count = 30,
    color = '#F5C97B',
    minSize = 0.6,
    maxSize = 1.2,
    wind = 0.15,
    speed = 1,
    className = '',
}) => {
    const mountRef = useRef(null);

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return;

        const width = mount.clientWidth;
        const height = mount.clientHeight;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 100);
        camera.position.set(0, 0, 12);

        const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.setSize(width, height);
        mount.appendChild(renderer.domElement);

        const ambient = new THREE.AmbientLight(0xffffff, 1.1);
        const dirLight = new THREE.DirectionalLight(0xfff2d8, 0.55);
        dirLight.position.set(3, 5, 4);
        const fillLight = new THREE.DirectionalLight(0xffffff, 0.4);
        fillLight.position.set(-3, -2, -3);
        scene.add(ambient, dirLight, fillLight);

        const worldWidth = 14;
        const worldHeight = 10;
        const depthRange = 6;

        const allDisposables = { geometries: [], materials: [] };

        const petals = Array.from({ length: count }, () => {
            const size = randomBetween(minSize, maxSize);
            const model = createLotusModel(color);
            model.scale.setScalar(size);
            model.position.set(
                randomBetween(-worldWidth / 2, worldWidth / 2),
                randomBetween(-worldHeight / 2, worldHeight / 2),
                randomBetween(-depthRange / 2, depthRange / 2)
            );
            model.rotation.y = randomBetween(0, Math.PI * 2);
            model.rotation.z = randomBetween(-0.3, 0.3);
            scene.add(model);

            const disposables = collectDisposables(model);
            allDisposables.geometries.push(...disposables.geometries);
            allDisposables.materials.push(...disposables.materials);

            return {
                model,
                fallSpeed: randomBetween(0.008, 0.02) * speed,
                swaySpeed: randomBetween(0.5, 1.3),
                swayAmplitude: randomBetween(0.4, 1.2),
                swayOffset: randomBetween(0, Math.PI * 2),
                rotSpeedY: randomBetween(0.012, 0.026) * (Math.random() > 0.5 ? 1 : -1),
                wobbleSpeed: randomBetween(0.6, 1.4),
                wobbleOffset: randomBetween(0, Math.PI * 2),
            };
        });

        let elapsed = 0;
        let rafId;
        const animate = () => {
            elapsed += 1;
            for (const p of petals) {
                p.model.position.y -= p.fallSpeed;
                p.model.position.x += Math.sin(elapsed * 0.02 * p.swaySpeed + p.swayOffset) * 0.01 * p.swayAmplitude + wind * 0.01;
                p.model.rotation.y += p.rotSpeedY;
                // 花朵飄落時輕微前後搖擺（繞 z 軸小幅擺動），模擬真實花朵飄落的晃動感，
                // 幅度小不會造成上下翻面
                p.model.rotation.z = Math.sin(elapsed * 0.02 * p.wobbleSpeed + p.wobbleOffset) * 0.15;

                if (p.model.position.y < -worldHeight / 2 - 1) {
                    p.model.position.y = worldHeight / 2 + 1;
                    p.model.position.x = randomBetween(-worldWidth / 2, worldWidth / 2);
                }
                if (p.model.position.x > worldWidth / 2 + 1) p.model.position.x = -worldWidth / 2 - 1;
                if (p.model.position.x < -worldWidth / 2 - 1) p.model.position.x = worldWidth / 2 + 1;
            }
            renderer.render(scene, camera);
            rafId = requestAnimationFrame(animate);
        };
        animate();

        const handleResize = () => {
            const w = mount.clientWidth;
            const h = mount.clientHeight;
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
            renderer.setSize(w, h);
        };
        window.addEventListener('resize', handleResize);

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener('resize', handleResize);
            allDisposables.geometries.forEach((g) => g.dispose());
            allDisposables.materials.forEach((m) => m.dispose());
            renderer.dispose();
            if (mount.contains(renderer.domElement)) {
                mount.removeChild(renderer.domElement);
            }
        };
    }, [count, color, minSize, maxSize, wind, speed]);

    return (
        <div
            ref={mountRef}
            className={className}
            aria-hidden="true"
            style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 5,
                overflow: 'hidden',
            }}
        />
    );
};

FallingLotusModel3D.propTypes = {
    count: PropTypes.number,
    color: PropTypes.string,
    minSize: PropTypes.number,
    maxSize: PropTypes.number,
    wind: PropTypes.number,
    speed: PropTypes.number,
    className: PropTypes.string,
};

export default FallingLotusModel3D;
